'use client';

import { FC, useEffect, useState } from 'react';

import { useAuthentication, UserGroups } from '@ravago/shared/authentication';
import {
   AutoSuggestObjectTypeV2,
   AutoSuggestResponseContent,
   AutoSuggestV2ResponseContent,
   facetOrObjectTypeLabelTranslationMappingV2
} from '@ravago/shared/page-data/models/elastic';
import { DynamicPageUrl, RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { SearchItem } from '@ravago/shared/radiance/components/multifacet/components/SearchItem/SearchItem';
import { HighlightText } from '@ravago/shared/radiance/components/typography/components/HighlightText/HighlightText';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { CorrelationService } from '../../../../services/tracking/correlation-token.service';
import { TrackingUtils } from '../../../../utils/tracking/tracking.utils';
import { ConsumerWrapper } from '../../../content/consumer-wrapper/consumer-wrapper';
import { DynamicPageLink } from '../../../content/dynamic-page-link/dynamic-page-link';
import { MockLink } from '../../../content/mock-link/mock-link';

interface Props {
   suggestedPages: AutoSuggestV2ResponseContent[];
   itemCount: number;
   language: string;
   title?: string;
   highlightTerm?: string;
   baseRoutes?: {
      product: string;
      brand: string;
      caseStudy: string;
   };
   productLister?: boolean;
   globals: RendererGlobals;
}

export const AutoSuggestPagesV2: FC<Props> = ({ suggestedPages, itemCount, title, highlightTerm, language, baseRoutes, productLister, globals }) => {
   const translate = useTranslations();
   const { user } = useAuthentication();
   const isAuthenticated = !!user;

   const [visualSuggestedPages, setVisualSuggestedPages] = useState<AutoSuggestResponseContent[]>(productLister ? suggestedPages : []);

   const searchItemMapping: Record<
      AutoSuggestObjectTypeV2,
      {
         icon: string;
         color: 0 | 1 | 2;
         baseRoute?: DynamicPageUrl;
      }
   > = {
      caseStudy: {
         icon: 'book',
         color: 2,
         baseRoute: { url: baseRoutes?.caseStudy }
      },
      brand: {
         icon: 'industry',
         color: 1,
         baseRoute: { url: baseRoutes?.brand }
      },
      product: {
         icon: 'shopping-cart',
         color: 0,
         baseRoute: { url: baseRoutes?.product }
      }
   };

   useEffect(() => {
      const pages = suggestedPages.filter((item) => searchItemMapping[item.type as AutoSuggestObjectTypeV2].baseRoute);

      if (!isAuthenticated) {
         setVisualSuggestedPages(pages.filter((item) => item.type !== 'Case Study'));
      } else {
         setVisualSuggestedPages(pages);
      }
   }, [isAuthenticated, suggestedPages]);

   const getSubLabel = (content: AutoSuggestResponseContent): string => {
      if (content.group && content.subgroup) return `${content.group}/${content.subgroup}`;
      if (content.functionalKeyRequirements) return content.functionalKeyRequirements;
      if (content.type) return translate(facetOrObjectTypeLabelTranslationMappingV2.get(content.type) ?? content.type);
      return '';
   };

   return (
      <Box gap="lg" width="100%">
         <Box direction="column" gap="lg" width="100%">
            <Typography variant="heading-4" color={title ? 'strong' : undefined}>
               {title ?? translate('auto-suggest.pages')}
            </Typography>
            <Box direction="column" gap="xs" width="100%">
               {visualSuggestedPages.slice(0, itemCount).map((item) => (
                  <ConsumerWrapper
                     key={item.id}
                     isConsumer={globals.isConsumer}
                     renderConsumer={(children) => (
                        <DynamicPageLink
                           id={`dynamic-link__auto-suggest__${item.slug}`}
                           slug={item.slug ?? ''}
                           baseRoute={searchItemMapping[item.type as AutoSuggestObjectTypeV2].baseRoute ?? null}
                           locale={globals.locale}
                           underline="none"
                        >
                           {children}
                        </DynamicPageLink>
                     )}
                     renderProducer={(children) => <MockLink>{children}</MockLink>}
                  >
                     <SearchItem
                        onClick={() =>
                           TrackingUtils.track('autosuggest_page_click', {
                              correlationId: CorrelationService.getCorrelationId('dpcid'),
                              pageId: item.id?.toString() ?? 'null',
                              pageType: item.type,
                              ...(searchItemMapping[item.type as AutoSuggestObjectTypeV2].baseRoute && {
                                 baseRoute: searchItemMapping[item.type as AutoSuggestObjectTypeV2].baseRoute?.url
                              }),
                              ...(item.slug && { slug: item.slug }),
                              language
                           })
                        }
                        trailingIcon="chevron-right"
                        facetIcon={searchItemMapping[item.type as AutoSuggestObjectTypeV2].icon}
                        facetColor={searchItemMapping[item.type as AutoSuggestObjectTypeV2].color}
                     >
                        <Box direction="column" items="start" gap="3xs">
                           <Typography variant="small" color="primary">
                              <HighlightText text={item.label} searchValue={highlightTerm} />
                              {item.preferred === 1 && (user?.groups?.includes(UserGroups.ravagoSalesRepresentative) || !globals.isConsumer) && (
                                 <Icon icon="gem" color="inherit" size="small" />
                              )}
                              {item.quality === 'prime' && <Icon icon="leaf" color="inherit" size="small" />}
                           </Typography>
                           <Typography variant="small" color="subtle">
                              <HighlightText text={getSubLabel(item)} searchValue={highlightTerm} />
                           </Typography>
                        </Box>
                     </SearchItem>
                  </ConsumerWrapper>
               ))}
               {visualSuggestedPages.length === 0 && <Typography>{translate('auto-suggest.no-pages')}</Typography>}
            </Box>
         </Box>
      </Box>
   );
};
